import React from 'react';
import {
  Button,
  Gutter,
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Flashy,
  Checkbox,
  Form,
  Accolade,
  Icon,
  SectionHeader,
  CondensedLarge,
} from '@axiom/ui';
import type { Candidate, CandidateWorkFeedItem } from '@axiom/validation';

import { WorkFeedQualificationWarnings } from '../WorkFeedQualificationWarnings/WorkFeedQualificationWarnings';

import { WorkFeedQualificationWarningModalSchema } from './work-feed-qualification-warning-modal-schema';

export type WorkFeedQualificationWarningModalProps = {
  candidate: Candidate;
  name?: string;
  onAfterSubmit: (() => void) | (() => Promise<void>);
  onClose: () => void;
  workFeedItem: CandidateWorkFeedItem;
};

export const WorkFeedQualificationWarningModal = ({
  candidate,
  name = 'WORK_FEED_QUALIFICATION_WARNING_MODAL',
  onAfterSubmit,
  onClose,
  workFeedItem,
}: WorkFeedQualificationWarningModalProps) => {
  const isSelfService = workFeedItem.opportunity.selfService;
  return (
    <Form
      name={`${name}_FORM`}
      schema={WorkFeedQualificationWarningModalSchema}
      initialValues={{
        agree: false,
      }}
      onSubmit={async () => {
        await onAfterSubmit();
      }}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name={name}>
            <ModalHeader
              name="PENDO_WORK_FEED_QUALIFICATION_WARNING_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              {isSelfService
                ? `Submit profile to ${workFeedItem.opportunity.accountName ?? 'Client'}`
                : 'Submit profile to Axiom'}
            </ModalHeader>
            <ModalSection>
              {isSelfService ? (
                <Gutter bottom="16px">
                  <Accolade
                    color="purple"
                    label="Self-service"
                    name="SELF_SERVICE"
                  >
                    <Icon name="star-filled" />
                  </Accolade>
                </Gutter>
              ) : null}
              <SectionHeader>
                Your profile does not meet all opportunity requirements.
              </SectionHeader>
              <Gutter bottom="16px" />
              <CondensedLarge>
                {isSelfService
                  ? 'Self-Service submissions are immediately shared with the client so it’s important to make the right impression. '
                  : null}
                Please take one of the following actions:
              </CondensedLarge>
              <Gutter bottom="16px" />
              <ul>
                <li>
                  <CondensedLarge>
                    <Flashy bold>Strengthen your profile:</Flashy> Cancel this
                    submission, save the opportunity, and resubmit after
                    updating your profile. Address the details below, ensure all
                    experiences have detailed descriptions and skills, and
                    confirm you have enough weekly availability for this work.
                  </CondensedLarge>
                  <Gutter bottom="16px" />
                </li>
                <li>
                  <CondensedLarge>
                    <Flashy bold>Certify compliance:</Flashy> If you are certain
                    your profile meets all requirements despite the warning, you
                    may certify compliance and continue with the submission.
                  </CondensedLarge>
                </li>
              </ul>
              <WorkFeedQualificationWarnings
                candidate={candidate}
                workFeedItem={workFeedItem}
              />
              <Checkbox
                name="agree"
                displayValue="I certify that my experience meets the qualifications of this position."
              />
              <Gutter bottom="24px" only="smallScreen" />
            </ModalSection>
            <ModalFooter>
              <Button
                variation="outline"
                name="PENDO_WORK_FEED_QUALIFICATION_WARNING_MODAL_CANCEL"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>

              <Button
                name="PENDO_WORK_FEED_QUALIFICATION_WARNING_MODAL_SUBMIT"
                onClick={fireSubmit}
              >
                Continue
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
